import paper from 'paper';
import { v4 as uuidv4 } from 'uuid';
import Project from '@/components/whiteboard/project';
import ConnectorContact from '@/components/whiteboard/connector-contact';

export default class Connection {
    layer;
    group;
    path;
    circles;
    shadowCircles;
    shadowDrag = null;

    constructor(layer, paperItem) {
        let point1;
        let point2;

        this.layer = layer;
        this.layer.connections.push(this);

        this.group = new paper.Group();
        this.group.data.type = 'connection';


        this.path = new paper.Path({
            strokeColor: '#000000',
            strokeWidth: 3,
        });

        if (Array.isArray(paperItem) && paperItem[0] === 'Group') {
            this.group = paper.project.activeLayer.importJSON(paperItem);
            this.path = this.group.children[0];
            this.shadowCircles = this.group.children[1];
            this.circles = this.group.children[2];
        } else if (Array.isArray(paperItem)) {
            let coordinates = paperItem;
            point1 = new paper.Point(coordinates[0], coordinates[1]);
            point2 = new paper.Point(coordinates[0], coordinates[1]);

            this.createPaperItem(point1, point2);
        } else {
            point1 = new paper.Point(20, 20);
            point2 = new paper.Point(100, 100);

            this.createPaperItem(point1, point2);
        }

        this.group.onMouseDrag = this.onDrag.bind(this);
        this.group.onMouseDown = this.onDown.bind(this);
        this.group.onMouseUp = this.onUp.bind(this);
        this.initShadowCircles();
        this.initCircles();
    }

    createPaperItem(point1, point2) {
        this.path.add(point1, point2);
        this.path.smooth({ type: 'geometric' });
        this.group.addChild(this.path);

        this.shadowCircles = new paper.Group();
        this.group.addChild(this.shadowCircles);

        this.circles = new paper.Group();
        this.group.addChild(this.circles);
    }

    initCircles() {
        this.circles.removeChildren();

        for (let i = 0; i < this.path.segments.length; i++) {
            let circle = new paper.Path.Circle({
                center: this.path.segments[i].point,
                fillColor: '#ff0000',
                radius: 4
            });

            circle.onMouseDrag = this.onCircleDrag.bind(this);
            circle.onDoubleClick = this.onDoubleClick.bind(this);

            this.circles.addChild(circle);
        }
    }

    initShadowCircles() {
        this.shadowCircles.removeChildren();

        for (let i = 0; i < this.path.segments.length - 1; i++) {
            let circle = new paper.Path.Circle({
                fillColor: '#0000ff',
                radius: 4
            });

            circle.onMouseDrag = this.onShadowDrag.bind(this);
            circle.onMouseUp = this.onShadowUp.bind(this);
            this.shadowCircles.addChild(circle);
        }

        this.repositionShadowCircles();
    }

    repositionShadowCircles() {
        for (let i = 0; i < this.shadowCircles.children.length; i++) {
            let middle = this.path.curves[i].getPointAt(this.path.curves[i].length / 2);

            this.shadowCircles.children[i].position = middle;
        }
    }

    hide() {
        this.group.visible = false;
    }

    show() {
        this.group.visible = true;
    }

    toSerializable() {
        return JSON.parse(this.group.exportJSON());
    }

    destroy() {
        this.group.remove();
    }

    moveDestination(x, y) {
        this.movePoint(this.path.segments.length - 1, x, y);
    }

    movePoint(cornerIndex, x, y, remotely) {
        let segment = this.path.segments[cornerIndex];
        segment.point.x = x;
        segment.point.y = y;
        this.path.smooth({ type: 'geometric' });

        this.circles.children[cornerIndex].position = segment.point;

        if (!remotely && (segment.isFirst() || segment.isLast())) {
            this.checkForASnap(segment);
        }

        this.repositionShadowCircles();
    }

    checkForASnap(segment) {
        let nearestNode = null;
        let nearestPath = null;
        let shortestNodeDistance = null;
        let nearestCurve = null;
        let shortestCurveDistance = null;
        let nearestPoint = null;
        let nodes = this.layer.nodes;

        for (let i = 0; i < nodes.length; i++) {
            let nearestPoint = nodes[i].path.getNearestPoint(segment.point);
            let distance = nearestPoint.getDistance(segment.point);

            if (shortestNodeDistance === null || shortestNodeDistance > distance) {
                shortestNodeDistance = distance;
                nearestNode = nodes[i];
                nearestPath = nearestNode.path;
            }
        }

        for (let i = 0; i < nearestPath.curves.length; i++) {
            let curve = nearestPath.curves[i];
            let point = curve.getNearestPoint(segment.point);
            let distance = point.getDistance(segment.point);

            if (shortestCurveDistance === null || shortestCurveDistance > distance) {
                shortestCurveDistance = distance;
                nearestCurve = curve;
                nearestPoint = point;
            }
        }

        if (nearestPoint !== null && nearestPoint.getDistance(segment.point) < 10) {
            this.snap(segment, nearestPath, nearestCurve, nearestPoint);
        } else {
            this.unsnap(segment);
        }
    }

    snap(segment, nearestPath, nearestCurve, nearestPoint) {
        let segmentPosition;
        segment.point = nearestPoint;
        this.circles.children[segment.index].position = nearestPoint;

        if (segment.isFirst()) {
            segmentPosition = 'first';
        } else if (segment.isLast()) {
            segmentPosition = 'last';
        } else {
            throw 'Trying to connect a middle segment!';
        }

        if (!segment.path.data.uuid) {
            segment.path.data.uuid = uuidv4();
        }

        let contact = new ConnectorContact(nearestCurve.index, nearestCurve.getLocationOf(nearestPoint).time, segment.path.data.uuid, segmentPosition);
        if (!nearestPath.data.contacts) {
            nearestPath.data.contacts = [];
        }

        let existingContactIndex = null;
        for (let i = 0; i < nearestPath.data.contacts.length; i++) {
            let c = nearestPath.data.contacts[i];

            if (c.connectionId === segment.path.data.uuid &&
                (
                    (c.connectionSegmentPosition === 'first' && segment.isFirst())
                    || (c.connectionSegmentPosition === 'last' && segment.isLast())
                )
            ) {
                existingContactIndex = i;

                break;
            }
        }

        if (existingContactIndex !== null) {
            nearestPath.data.contacts.splice(existingContactIndex, 1, contact)
        } else {
            nearestPath.data.contacts.push(contact);
        }

        if (!nearestPath.data.uuid) {
            nearestPath.data.uuid = uuidv4();
        }

        if (segmentPosition === 'first') {
            segment.path.data.firstConnectedTo = nearestPath.data.uuid;
        } else if (segmentPosition === 'last') {
            segment.path.data.lastConnectedTo = nearestPath.data.uuid;
        }
    }

    unsnap(segment) {
        let target;

        if (segment.isFirst()) {
            target = segment.path.data.firstConnectedTo;
            segment.path.data.firstConnectedTo = null;
        } else if (segment.isLast()) {
            target = segment.path.data.lastConnectedTo
            segment.path.data.lastConnectedTo = null;
        } else {
            throw 'Trying to disconnect a middle segment!';
        }

        if (!target) {
            return;
        }

        let nodes = this.layer.nodes;

        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].path.data.uuid === target) {
                let segmentPosition;
                if (segment.isFirst()) {
                    segmentPosition = 'first';
                } else if (segment.isLast()) {
                    segmentPosition = 'last';
                }

                for (let m = 0; m < nodes[i].path.data.contacts.length; m++) {
                    let contact = nodes[i].path.data.contacts[m];

                    if (contact.connectionId === segment.path.data.uuid && contact.connectionSegmentPosition === segmentPosition) {
                        nodes[i].path.data.contacts.splice(m, 1);

                        break;
                    }
                }

                break;
            }
        }
    }

    onDrag(e) {
        if (this.layer.project.mode !== Project.MODE_DEFAULT) {
            return;
        }

        if (this.path.data.firstConnectedTo || this.path.data.lastConnectedTo) {
            return;
        }

        this.group.position.x += e.delta.x;
        this.group.position.y += e.delta.y;
    }

    onDown() {
        if (this.layer.project.mode !== Project.MODE_DEFAULT) {
            return;
        }

        this.originalIndex = this.group.index;
        this.group.bringToFront();
    }

    onUp() {
        if (this.layer.project.mode !== Project.MODE_DEFAULT) {
            return;
        }

        this.group.parent.insertChild(this.originalIndex, this.group);
    }

    onCircleDrag(e) {
        e.stopPropagation();

        this.movePoint(e.target.index, e.point.x, e.point.y);
    }

    onDoubleClick(e) {
        e.stopPropagation();

        let index = e.target.index;
        let segment = this.path.segments[index];

        if (segment.previous == null || segment.next == null) {
            return;
        }

        this.path.removeSegment(index);

        this.initCircles();
        this.initShadowCircles();
    }

    onShadowUp(e) {
        e.stopPropagation();

        this.shadowDrag = null;
    }

    onShadowDrag(e) {
        e.stopPropagation();

        if (this.shadowDrag == null) {
            let index = this.shadowCircles.children.indexOf(e.target);
            let curve = this.path.curves[index];
            curve.divideAt(curve.length / 2);
            this.shadowDrag = index;

            this.initCircles();
            this.initShadowCircles();
        } else {
            this.movePoint(this.shadowDrag + 1, e.point.x, e.point.y);
        }
    }
}
